import React, { useState, useEffect } from 'react';
import './olx_sell.scss';
import { useLocation } from "react-router-dom";
import axios from 'axios';
import moment from 'moment';
import APP_BASE_URL from '../../components/Api/globalApi';

const SellPage = () => {
	const [apPoints, setApPoints] = useState('');
	const [price, setPrice] = useState('');
	const [confirmation, setConfirmation] = useState(false);
	const [activeListings, setActiveListings] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [currentAP, setCurrentAP] = useState(0);
	const [multiplier, setMultiplier] = useState(1);
	const baseValue = 1200;
	const location = useLocation();
	const income = location.state?.data;

	// Fetch user AP balance and listings on component mount
	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				// Fetch AP balance
				const apResponse = await axios.get(
					`${APP_BASE_URL}/myincome`,
					{
						headers: {
							user_id: localStorage.getItem("user_id"),
						},
					}
				);

				if (apResponse.data.result) {
					setCurrentAP(apResponse.data.data.u_activity_point || 0);
				}

				// Fetch active listings
				const listingsResponse = await axios.get(
					`${APP_BASE_URL}/sell/list`,
					{
						headers: {
							user_id: localStorage.getItem("user_id"),
						},
					}
				);

				if (listingsResponse.data.result) {
					setActiveListings(listingsResponse.data.data || []);
				}
			} catch (error) {
				console.error("Failed to fetch data:", error);
				// Fallback to location state if API fails
				if (income?.u_activation_point) {
					setCurrentAP(income.u_activation_point);
				}
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	}, []);

	const handleSell = () => {
		const ap = parseInt(apPoints);
		const priceValue = parseFloat(price);
		if (!ap || !priceValue) {
			alert("Please fill in all fields with valid values!");
			return;
		}

		if (ap > currentAP) {
			alert("You don't have enough AP to sell!");
			return;
		}

		if (ap <= 0 || priceValue <= 0) {
			alert("Values must be greater than zero!");
			return;
		}

		setConfirmation(true);
	};

	const confirmSell = async () => {
		try {
			setConfirmation(false);
			setIsLoading(true);

			const response = await axios.post(
				`${APP_BASE_URL}/add/sell`,
				{
					ap_point: apPoints,
					price: price
				},
				{
					headers: {
						user_id: localStorage.getItem("user_id"),
					},
				}
			);

			if (response.data.result) {
				// Update local state without reload
				const newAP = currentAP - parseInt(apPoints);
				setCurrentAP(newAP);

				alert(`Successfully listed ${apPoints} AP for $${price}`);
				setApPoints('');
				setPrice('');
				window.location.reload()
			} else {
				alert(response.data.message || 'Failed to list AP');
			}
		} catch (error) {
			console.error("Listing error:", error);
			alert("An error occurred while listing. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};

	const cancelListing = async (listingId) => {
		if (!window.confirm("Are you sure you want to cancel this listing?")) return;

		try {
			setIsLoading(true);
			const response = await axios.post(
				`${APP_BASE_URL}/sell/cancel`,
				{
					ao_id: listingId
				},
				{
					headers: {
						user_id: localStorage.getItem("user_id"),
					},
				}
			);

			if (response.data.result) {
				// Update local state without reload
				const cancelledListing = activeListings.find(l => l.ao_id === listingId);
				if (cancelledListing) {
					setCurrentAP(prev => prev + cancelledListing.ao_ap);
					setActiveListings(prev => prev.filter(l => l.ao_id !== listingId));
				}
				alert(response.data.message);
			}
		} catch (error) {
			console.error("Cancellation error:", error);
			alert("Failed to cancel listing. Please try again.");
		} finally {
			setIsLoading(false);
		}
	};
	const handleIncrement = () => {
		const newMultiplier = multiplier + 1;
		setMultiplier(newMultiplier);
		setApPoints(baseValue * newMultiplier);
	};
	const handleDecrement = () => {
		if (multiplier > 1) {
			const newMultiplier = multiplier - 1;
			setMultiplier(newMultiplier);
			setApPoints(baseValue * newMultiplier);
		}
	};

	return (
		<div className="sell-page">
			<h2 className="sell-heading">Sell Activity Points</h2>

			<div className="ap-balance-box">
				<span>Current AP Balance: </span>
				<strong>{currentAP} AP</strong>
			</div>

			<div className="sell-form">
				<input
					type="number"
					placeholder="1200"
					value={apPoints}
					readOnly
					min="1"
				/>
				<button onClick={handleIncrement} style={{ padding: '4px 10px' }}>↑</button>
				<button onClick={handleDecrement} style={{ padding: '4px 10px' }}>↓</button>
				<input
					type="number"
					placeholder="Set your price ($)"
					value={price}
					onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ''))}
					min="0.01"
					step="0.01"
				/>
				<button onClick={handleSell} disabled={isLoading}>
					{isLoading ? 'Processing...' : 'List AP for Sale'}
				</button>
			</div>

			<div className="active-listings">
				<h3>Your Active Listings</h3>
				{isLoading ? (
					<div className="loading">Loading...</div>
				) : activeListings.length === 0 ? (
					<div className="no-listings">No active listings</div>
				) : (
					<div className="listings-container">
						{activeListings.map((listing) => (
							<div key={listing.ao_id} className="listing-card">
								<div className="listing-details">
									<span>{listing.ao_ap} AP</span>
									<span>${listing.ao_price}</span>
									<span>{listing.referal_id}</span>
									{listing.ao_status === 'cancelled' ? (
										<span>{listing.ao_status}</span>
									) : null}
									<span>Listed on: {moment(listing.ao_date).format("YYYY-MM-DD")}</span>
								</div>
								{listing.ao_status === 'active' ? (
									<button
										onClick={() => cancelListing(listing.ao_id)}
										className="cancel-btn"
										disabled={isLoading}
									>
										{isLoading ? 'Processing...' : 'Cancel'}
									</button>
								) : null}


							</div>
						))}
					</div>
				)}
			</div>

			{confirmation && (
				<div className="modal-overlay" onClick={() => setConfirmation(false)}>
					<div className="modal-content" onClick={(e) => e.stopPropagation()}>
						<h3>Confirm Listing</h3>
						<p>You are selling {apPoints} AP for ${price}</p>
						<div className="modal-actions">
							<button
								onClick={() => setConfirmation(false)}
								className="cancel-btn"
								disabled={isLoading}
							>
								Cancel
							</button>
							<button
								onClick={confirmSell}
								className="confirm-btn"
								disabled={isLoading}
							>
								{isLoading ? 'Processing...' : 'Confirm'}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default SellPage;